import { useEffect, useState } from 'react';
import type {
  ApiClient, Kabeltrassenbestand, Kabeltrassenblatt,
} from '../api';
import {
  DataTable, EmptyState, ErrorState, LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Der Bestand der Kabeltrassen — als Baum.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE KATEGORIE GANZ OBEN STEHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sie ist die gröbste Trennung: Eine Kabelrinne, ein Leerrohr und ein
 * Fundamenterder haben nichts miteinander zu tun. Wer Rinnen sucht, will nicht
 * durch 13.723 Erdungsbauteile blättern.
 *
 * Darunter die Ortsordnung — Ebene, Abschnitt, Raum. Genau die Reihenfolge, in
 * der gearbeitet wird.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE EBENE UND NICHT DAS ROHGESCHOSS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ausgezählt am 2026-08-17: 19 Geschosswerte für acht Etagen. `_OKRB`,
 * `_UKRD` und `_ARC` sind Revit-Ebenenvarianten und keine Stockwerke — ein
 * Baum danach hat 19 Äste, und die Trassen einer Etage liegen in dreien
 * davon. Genau das machte die Seite unlesbar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SEITENBLÄTTER STATT STILLER KAPPUNG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Liste endete vorher bei 2.000 Zeilen, ohne es zu sagen. Ein Knoten mit
 * 19.770 Bauteilen zeigte 2.000 und sah vollständig aus. Jetzt steht die
 * Gesamtzahl daneben, und wer mehr will, blättert.
 */

const zahl = (n: number) => n.toLocaleString('de-DE');

const meter = (n: number) =>
  n.toLocaleString('de-DE', { maximumFractionDigits: 1 });

type Auswahl = {
  gruppe: string;
  ebene?: string;
  abschnitt?: string;
  raum?: string;
  titel: string;
};

export function Trassenbestand(
  { api, projektId }: { api: ApiClient; projektId: string },
) {
  const [b, setB] = useState<Kabeltrassenbestand | null>(null);
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const [gewaehlt, setGewaehlt] = useState<Auswahl | null>(null);
  const [blatt, setBlatt] = useState<Kabeltrassenblatt | null>(null);
  const [seite, setSeite] = useState(1);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.kabeltrassenbestand(projektId)
      .then(setB)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  // Ein Wechsel des Knotens beginnt wieder auf Blatt 1. Sonst stünde nach
  // dem Klick auf einen kleinen Ast „Seite 7 von 1" und eine leere Liste.
  useEffect(() => { setSeite(1); }, [gewaehlt]);

  useEffect(() => {
    if (!gewaehlt) { setBlatt(null); return; }

    let weg = false;
    setLaedt(true);

    api.kabeltrassenliste(projektId, {
      gruppe: gewaehlt.gruppe,
      ebene: gewaehlt.ebene,
      planungsbereich: gewaehlt.abschnitt,
      raum: gewaehlt.raum,
      seite,
      proSeite: 100,
    })
      .then((x) => { if (!weg) setBlatt(x); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => { if (!weg) setLaedt(false); });

    return () => { weg = true; };
  }, [api, projektId, gewaehlt, seite]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Der Trassenbestand konnte nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!b) return <LoadingState text="Trassenbestand wird geladen …" zeilen={10} spalten={4} />;

  const schalten = (k: string) => setOffen((o) => ({ ...o, [k]: !o[k] }));

  const baum = b.baum ?? [];

  const spalten: Spalte<any>[] = [
    { schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (t) => t.typ },
    {
      // Der Servicetyp und NICHT der Systemname: Der ist bei 0 von 50.409
      // gepflegt, und das ist eine Aussage, kein fehlender Wert.
      schluessel: 'system', titel: 'System', wert: (t) => t.systemtyp,
      inhalt: (t) => <span className="schwach">{t.systemtyp ?? <em>—</em>}</span>,
    },
    {
      schluessel: 'stromart', titel: 'Stromart', art: 'status', breite: '190px',
      wert: (t) => t.stromart,
      inhalt: (t) => (t.stromart === 'unbestimmt'
        ? <StatusBadge zustand="warnung" text="unbestimmt" klein />
        : <StatusBadge zustand="info" text={t.stromart} klein />),
    },
    {
      schluessel: 'querschnitt', titel: 'Querschnitt', ausrichtung: 'rechts',
      wert: (t) => t.breiteMm ?? t.durchmesserMm,
      inhalt: (t) => <>{querschnitt(t)}</>,
    },
    {
      schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm',
      nachkomma: 1, wert: (t) => t.laengeM,
    },
    {
      schluessel: 'raum', titel: 'Raum', wert: (t) => t.raumnummer,
      titelHinweis: '≈ bedeutet: über die Nachbarsuche zugeordnet, nicht aus dem Raumvolumen',
      inhalt: (t) => (
        <span className="schwach" style={{ fontSize: 12 }}>
          {t.raumnummer
            ? (
              <>
                {t.raumnummer}
                {t.raumHerkunft === 'nachbar' && (
                  <span title={'Nachbarsuche — eine Vermutung zweiter Ordnung. Die '
                    + 'Trasse liegt über dem Raumvolumen, etwa im Deckenhohlraum.'}>
                    {' '}≈
                  </span>
                )}
              </>
            )
            : '—'}
        </span>
      ),
    },
    {
      schluessel: 'revitId', titel: 'Revit-Id', wert: (t) => t.revitElementId,
      inhalt: (t) => <span className="schwach">{t.revitElementId ?? '—'}</span>,
    },
  ];

  return (
    <>
      {(b.hinweise ?? []).map((h, i) => (
        <div key={i} className="hinweis" style={{ display: 'block' }}>{h}</div>
      ))}

      {baum.length === 0 ? (
        <EmptyState
          titel="Für dieses Modell ist noch keine Kabeltrasse aufgenommen."
          erklaerung="Die Trassen entstehen bei der nächsten Elektroaufnahme; der Elektrolauf liest Querschnitt und Merkmale selbst."
        />
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start',
                      flexWrap: 'wrap' }}>
          {/* ─── Der Baum ─────────────────────────────────────────────── */}
          <div className="karte"
               style={{ flex: '1 1 420px', maxHeight: '74vh',
                        overflowY: 'auto' }}>
            <h4 style={{ marginTop: 0 }}>
              Kategorie → Ebene → Abschnitt → Raum
            </h4>

            {b.baum.map((kat) => {
              const kK = `k:${kat.gruppe}`;

              return (
                <div key={kK}>
                  <button
                    onClick={() => schalten(kK)}
                    style={{
                      ...zeile,
                      fontWeight: 600,
                      borderTop: '1px solid var(--linie)',
                      // Was keine Kabelführung ist, steht blasser: Erdung
                      // und Dosen sind kein Arbeitsvorrat.
                      opacity: kat.istKabelweg ? 1 : 0.62,
                    }}>
                    <Pfeil auf={offen[kK]} />
                    <span>{kat.bezeichnung}</span>
                    <span style={rechts}>
                      {zahl(kat.anzahl)}
                      {kat.laengeM > 0 && ` · ${meter(kat.laengeM)} m`}
                    </span>
                  </button>

                  {offen[kK] && kat.ebenen.map((eb) => {
                    const eK = `${kK}|e:${eb.ebene}`;

                    return (
                      <div key={eK}>
                        <button onClick={() => schalten(eK)}
                                style={{ ...zeile, paddingLeft: 26 }}>
                          <Pfeil auf={offen[eK]} />
                          <span>{eb.ebene}</span>
                          <span style={rechts}>
                            {zahl(eb.anzahl)}
                            {eb.laengeM > 0 && ` · ${meter(eb.laengeM)} m`}
                          </span>
                        </button>

                        {offen[eK] && eb.abschnitte.map((ab) => {
                          const aK = `${eK}|a:${ab.abschnitt}`;

                          // Nur EIN Raum und der ist leer? Dann ist die
                          // vierte Ebene keine Ordnung, sondern eine leere
                          // Zeile. Der Abschnitt wird dann direkt anwählbar.
                          const nurLeer = ab.raeume.length === 1
                                       && ab.raeume[0].raumnummer === null;

                          const ist = gewaehlt?.gruppe === kat.gruppe
                                   && gewaehlt?.ebene === eb.ebene
                                   && gewaehlt?.abschnitt === ab.abschnitt
                                   && !gewaehlt?.raum;

                          return (
                            <div key={aK}>
                              <button
                                onClick={() => {
                                  if (nurLeer)
                                    setGewaehlt({
                                      gruppe: kat.gruppe,
                                      ebene: eb.ebene,
                                      abschnitt: ab.abschnitt,
                                      titel: `${kat.bezeichnung} · `
                                           + `${eb.ebene} · ${ab.abschnitt}`,
                                    });
                                  else schalten(aK);
                                }}
                                style={{
                                  ...zeile,
                                  paddingLeft: 48,
                                  background: ist
                                    ? 'var(--flaeche)' : 'transparent',
                                  boxShadow: ist
                                    ? 'inset 2px 0 0 var(--akzent)'
                                    : undefined,
                                }}>
                                {nurLeer ? <span style={{ width: 14 }} />
                                         : <Pfeil auf={offen[aK]} />}
                                <span>{ab.abschnitt}</span>
                                <span style={rechts}>
                                  {zahl(ab.anzahl)}
                                  {ab.laengeM > 0
                                    && ` · ${meter(ab.laengeM)} m`}
                                </span>
                              </button>

                              {!nurLeer && offen[aK] && ab.raeume.map((r) => {
                                const name = r.raumnummer
                                  ? `${r.raumnummer} ${r.raumname ?? ''}`.trim()
                                  : '(ohne Raum)';

                                const gew = gewaehlt?.gruppe === kat.gruppe
                                         && gewaehlt?.ebene === eb.ebene
                                         && gewaehlt?.abschnitt === ab.abschnitt
                                         && gewaehlt?.raum
                                            === (r.raumnummer ?? '(ohne Raum)');

                                return (
                                  <button
                                    key={`${aK}|r:${r.raumnummer ?? '~'}`}
                                    onClick={() => setGewaehlt({
                                      gruppe: kat.gruppe,
                                      ebene: eb.ebene,
                                      abschnitt: ab.abschnitt,
                                      raum: r.raumnummer ?? '(ohne Raum)',
                                      titel: `${kat.bezeichnung} · `
                                           + `${eb.ebene} · ${ab.abschnitt} · `
                                           + name,
                                    })}
                                    style={{
                                      ...zeile,
                                      paddingLeft: 70,
                                      fontSize: 12.5,
                                      background: gew
                                        ? 'var(--flaeche)' : 'transparent',
                                      boxShadow: gew
                                        ? 'inset 2px 0 0 var(--akzent)'
                                        : undefined,
                                      color: r.raumnummer
                                        ? undefined : 'var(--text-schwach)',
                                    }}>
                                    <span>{name}</span>
                                    <span style={rechts}>
                                      {zahl(r.anzahl)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* ─── Die Liste ────────────────────────────────────────────── */}
          <div style={{ flex: '2 1 560px' }}>
            {!gewaehlt ? (
              <EmptyState
                titel="Noch kein Zweig gewählt."
                erklaerung="Links einen Zweig aufklappen und einen Abschnitt oder Raum wählen — dann stehen hier seine Bauteile."
              />
            ) : (
              <>
                <DataTable
                  name={gewaehlt.titel}
                  spalten={spalten}
                  zeilen={blatt?.zeilen ?? []}
                  schluessel={(t) => String(t.id)}
                  laedt={laedt && !blatt}
                  // Geblättert wird über den SERVER (100 je Blatt). Die
                  // Tabelle zählt deshalb nicht selbst, sondern nennt die
                  // Gesamtzahl aus der Antwort.
                  proSeite={0}
                  trefferText={blatt
                    ? `${zahl(blatt.gesamt)} Bauteil(e) · Blatt ${blatt.seite} von ${blatt.seiten}`
                    : undefined}
                  ausfuhr
                  ausfuhrDateiname="trassenbestand"
                  dicht
                  leer={(
                    <EmptyState
                      titel="In diesem Zweig steht kein Bauteil."
                      erklaerung="Der Baum zählt den ganzen Ast; ein einzelner Raum darunter kann trotzdem leer sein."
                    />
                  )}
                />

                {blatt && blatt.seiten > 1 && (
                  <Blaetterleiste
                    seite={blatt.seite}
                    seiten={blatt.seiten}
                    umblaettern={setSeite}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Die Blätterleiste.
 *
 * Erste und letzte Seite ausdrücklich: Bei 198 Blättern ist „ans Ende" sonst
 * ein Dauerklicken, und genau dort steht, was zuletzt eingelesen wurde.
 */
function Blaetterleiste(
  { seite, seiten, umblaettern }:
  { seite: number; seiten: number; umblaettern: (n: number) => void },
) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center',
                  marginTop: 12, flexWrap: 'wrap' }}>
      <button className="m-knopf" disabled={seite <= 1} onClick={() => umblaettern(1)}>
        ⏮ erste
      </button>
      <button disabled={seite <= 1} onClick={() => umblaettern(seite - 1)}>
        ◀ zurück
      </button>

      <span className="schwach" style={{ fontSize: 12.5, margin: '0 8px' }}>
        Blatt {seite} von {seiten}
      </span>

      <button disabled={seite >= seiten}
              onClick={() => umblaettern(seite + 1)}>
        weiter ▶
      </button>
      <button disabled={seite >= seiten} onClick={() => umblaettern(seiten)}>
        letzte ⏭
      </button>
    </div>
  );
}

function Pfeil({ auf }: { auf?: boolean }) {
  return <span style={{ width: 14, display: 'inline-block' }}>
    {auf ? '▾' : '▸'}
  </span>;
}

/**
 * Der Querschnitt — Breite mal Höhe bei der Rinne, Durchmesser beim Leerrohr.
 *
 * Ein fehlender Querschnitt steht als Strich und nicht als 0: Die Null wäre
 * eine Messung, der Strich ist die fehlende Angabe.
 */
function querschnitt(t: {
  breiteMm: number | null; hoeheMm: number | null;
  durchmesserMm?: number | null;
}) {
  if (t.breiteMm != null && t.hoeheMm != null)
    return `${zahl(t.breiteMm)} × ${zahl(t.hoeheMm)} mm`;

  if (t.durchmesserMm != null) return `⌀ ${zahl(t.durchmesserMm)} mm`;

  return <span className="schwach">—</span>;
}

const zeile: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  width: '100%',
  padding: '6px 12px',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  fontSize: 13,
  cursor: 'pointer',
  color: 'var(--text)',
};

const rechts: React.CSSProperties = {
  marginLeft: 'auto',
  fontSize: 11,
  color: 'var(--text-schwach)',
  whiteSpace: 'nowrap',
};
